import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Chart, ChartConfiguration, ChartType, registerables } from 'chart.js';
import { GetRegistrantsViewItem } from 'edu-application/dist/registration/view-registrants/view-registrants.viewmodel';

Chart.register(...registerables);

@Component({
  selector: 'app-daily-registrants-chart',
  templateUrl: './daily-registrants-chart.component.html',
  styleUrls: ['./daily-registrants-chart.component.scss']
})
export class DailyRegistrantsChartComponent implements OnInit, OnChanges {
  @Input() registrants: GetRegistrantsViewItem[] = [];
  @ViewChild('chartCanvas', { static: true }) chartCanvas!: ElementRef<HTMLCanvasElement>;

  private chart: Chart | null = null;

  constructor() { }

  ngOnInit(): void {
    this.createChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['registrants'] && !changes['registrants'].firstChange) {
      this.updateChart();
    }
  }

  private createChart(): void {
    if (!this.registrants || this.registrants.length === 0) {
      return;
    }

    const dailyData = this.processRegistrantsData();
    const ctx = this.chartCanvas.nativeElement.getContext('2d');

    if (!ctx) {
      return;
    }

    const config: ChartConfiguration = {
      type: 'line' as ChartType,
      data: {
        labels: dailyData.labels,
        datasets: [{
          label: 'Daily Registrations',
          data: dailyData.data,
          borderColor: '#07429c',
          backgroundColor: 'rgba(7, 66, 156, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#07429c',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              font: {
                family: 'Inter, system-ui, sans-serif',
                size: 12,
                weight: 'normal'
              },
              color: '#374151'
            }
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            cornerRadius: 8,
            padding: 12,
            displayColors: false,
            callbacks: {
              title: function(context) {
                return `Date: ${context[0].label}`;
              },
              label: function(context) {
                const count = context.parsed.y;
                return `${count} registration${count !== 1 ? 's' : ''}`;
              }
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Date',
              font: {
                family: 'Inter, system-ui, sans-serif',
                size: 12,
                weight: 'bold'
              },
              color: '#374151'
            },
            grid: {
              display: false
            },
            ticks: {
              color: '#6B7280',
              font: {
                family: 'Inter, system-ui, sans-serif',
                size: 11
              },
              maxRotation: 45
            }
          },
          y: {
            title: {
              display: true,
              text: 'Number of Registrations',
              font: {
                family: 'Inter, system-ui, sans-serif',
                size: 12,
                weight: 'bold'
              },
              color: '#374151'
            },
            beginAtZero: true,
            grid: {
              color: 'rgba(0, 0, 0, 0.1)'
            },
            ticks: {
              color: '#6B7280',
              font: {
                family: 'Inter, system-ui, sans-serif',
                size: 11
              },
              stepSize: 1
            }
          }
        },
        interaction: {
          intersect: false,
          mode: 'index'
        }
      }
    };

    this.chart = new Chart(ctx, config);
  }

  private updateChart(): void {
    if (this.chart) {
      const dailyData = this.processRegistrantsData();
      this.chart.data.labels = dailyData.labels;
      this.chart.data.datasets[0].data = dailyData.data;
      this.chart.update();
    } else {
      this.createChart();
    }
  }

  private processRegistrantsData(): { labels: string[], data: number[] } {
    if (!this.registrants || this.registrants.length === 0) {
      return { labels: [], data: [] };
    }

    // Group registrants by date
    const dailyCounts: { [key: string]: number } = {};

    this.registrants.forEach(registrant => {
      const date = new Date(registrant.date);
      const dateString = date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric'
      });
      
      if (dailyCounts[dateString]) {
        dailyCounts[dateString]++;
      } else {
        dailyCounts[dateString] = 1;
      }
    });

    // Sort dates chronologically
    const sortedDates = Object.keys(dailyCounts).sort((a, b) => {
      const dateA = new Date(a + ', ' + new Date().getFullYear());
      const dateB = new Date(b + ', ' + new Date().getFullYear());
      return dateA.getTime() - dateB.getTime();
    });

    const labels = sortedDates;
    const data = sortedDates.map(date => dailyCounts[date]);

    return { labels, data };
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }
  }
}
